import { useState, type MouseEvent } from "react";
import {
  Badge,
  Box,
  ButtonBase,
  Divider,
  IconButton,
  Popover,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import { Bell } from "lucide-react";
import { useNotifications } from "modules/notifications/hooks/useNotifications";
import type {
  NotificationItem,
  NotificationSeverity,
} from "modules/notifications/model/notificationTypes";

const SEVERITY_COLORS: Record<NotificationSeverity, string> = {
  info: "#0ea5e9",
  success: "#22c55e",
  warning: "#f59e0b",
  error: "#ef4444",
};

// A notificacao vem do servidor e o linkUrl era passado direto pro
// window.location.assign, o que permitiria esquemas perigosos (javascript:,
// data:) e open-redirect pra outra origem. Só liberamos caminhos http(s) da
// MESMA origem, resolvendo relativos contra o host atual; caso contrario, null
// (nao navega).
function resolveSafeLinkUrl(rawUrl: string | null | undefined): string | null {
  if (!rawUrl) return null;
  const trimmed = rawUrl.trim();
  if (!trimmed || trimmed.startsWith("//")) return null;
  try {
    const parsed = new URL(trimmed, window.location.origin);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return null;
    if (parsed.origin !== window.location.origin) return null;
    return parsed.pathname + parsed.search + parsed.hash;
  } catch {
    return null;
  }
}

function formatRelative(iso: string | null | undefined): string {
  if (!iso) return "";
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return "";
  const diffSec = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (diffSec < 60) return "agora";
  const min = Math.floor(diffSec / 60);
  if (min < 60) return `${min} min atrás`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} h atrás`;
  const days = Math.floor(hr / 24);
  if (days < 30) return `${days} d atrás`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} mês${months > 1 ? "es" : ""} atrás`;
  return `${Math.floor(months / 12)} ano(s) atrás`;
}

export function NotificationBell() {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { items, unreadCount, isLoading, refresh, markAsRead, markAllAsRead } =
    useNotifications();

  const isOpen = Boolean(anchorEl);
  const popoverId = isOpen ? "notification-bell-popover" : undefined;

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    void refresh();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleItemClick = async (notification: NotificationItem) => {
    await markAsRead(notification);
    const safeUrl = resolveSafeLinkUrl(notification.linkUrl);
    if (safeUrl) {
      handleClose();
      window.location.assign(safeUrl);
    }
  };

  return (
    <>
      <Tooltip title="Notificações">
        <IconButton
          onClick={handleOpen}
          aria-label="Abrir notificações"
          aria-haspopup="true"
          aria-expanded={isOpen}
          aria-controls={popoverId}
          sx={(theme) => ({
            width: 40,
            height: 40,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            color: theme.palette.text.primary,
            bgcolor: alpha(theme.palette.primary.main, 0.04),
            "&:hover": {
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              borderColor: theme.palette.primary.main,
            },
          })}
        >
          <Badge
            badgeContent={unreadCount}
            max={99}
            color="error"
            overlap="circular"
            invisible={unreadCount === 0}
          >
            <Bell size={18} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        id={popoverId}
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: (theme) => ({
              mt: 1,
              width: { xs: "calc(100vw - 32px)", sm: 380 },
              maxWidth: "calc(100vw - 32px)",
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: theme.palette.background.paper,
              overflow: "hidden",
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0 24px 48px rgba(0, 0, 0, 0.5)"
                  : "0 24px 48px rgba(15, 23, 42, 0.12)",
            }),
          },
        }}
      >
        <Box
          sx={{
            px: 2.2,
            py: 1.4,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography
            sx={(theme) => ({
              color: theme.palette.text.primary,
              fontWeight: 700,
              fontSize: "1rem",
              lineHeight: 1.1,
            })}
          >
            Notificações{unreadCount > 0 ? ` (${unreadCount})` : ""}
          </Typography>
          {unreadCount > 0 ? (
            <ButtonBase
              onClick={() => void markAllAsRead()}
              sx={(theme) => ({
                color: theme.palette.primary.main,
                fontSize: 12,
                fontWeight: 600,
                px: 0.75,
                py: 0.25,
                borderRadius: 1,
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                },
              })}
            >
              Marcar todas como lidas
            </ButtonBase>
          ) : null}
        </Box>

        <Divider sx={(theme) => ({ borderColor: theme.palette.divider })} />

        <Box sx={{ maxHeight: 420, overflowY: "auto" }}>
          {items.length === 0 ? (
            <Box sx={{ px: 2.2, py: 4, textAlign: "center" }}>
              <Typography
                sx={(theme) => ({
                  color: theme.palette.text.secondary,
                  fontSize: 14,
                })}
              >
                {isLoading
                  ? "Carregando..."
                  : "Nenhuma notificação no momento."}
              </Typography>
            </Box>
          ) : (
            items.map((notification) => (
              <Box
                key={notification.id}
                onClick={() => void handleItemClick(notification)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    void handleItemClick(notification);
                  }
                }}
                sx={(theme) => ({
                  px: 2.2,
                  py: 1.4,
                  display: "flex",
                  gap: 1.4,
                  alignItems: "flex-start",
                  cursor: "pointer",
                  position: "relative",
                  bgcolor: notification.read
                    ? "transparent"
                    : alpha(theme.palette.primary.main, 0.05),
                  transition: "background-color 150ms ease",
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                  },
                  "&:focus-visible": {
                    outline: `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: -2,
                  },
                })}
              >
                {!notification.read ? (
                  <Box
                    sx={(theme) => ({
                      position: "absolute",
                      left: 8,
                      top: "50%",
                      transform: "translateY(-50%)",
                      width: 4,
                      height: 28,
                      borderRadius: 2,
                      bgcolor: theme.palette.primary.main,
                    })}
                  />
                ) : null}
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: SEVERITY_COLORS[notification.severity] ?? SEVERITY_COLORS.info,
                    mt: 0.8,
                    flex: "0 0 auto",
                  }}
                />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    sx={(theme) => ({
                      color: theme.palette.text.primary,
                      fontWeight: notification.read ? 500 : 700,
                      fontSize: 14,
                      lineHeight: 1.25,
                    })}
                  >
                    {notification.title}
                  </Typography>
                  {notification.body ? (
                    <Typography
                      sx={(theme) => ({
                        color: theme.palette.text.secondary,
                        mt: 0.3,
                        fontSize: 13,
                        lineHeight: 1.35,
                      })}
                    >
                      {notification.body}
                    </Typography>
                  ) : null}
                  <Typography
                    sx={(theme) => ({
                      color: theme.palette.text.disabled,
                      mt: 0.3,
                      fontSize: 11.5,
                      lineHeight: 1.1,
                    })}
                  >
                    {formatRelative(notification.createdAt)}
                  </Typography>
                </Box>
              </Box>
            ))
          )}
        </Box>
      </Popover>
    </>
  );
}
